<template>
  <div
    v-if="resources.length > 0"
    class="popular-link"
    :class="{
      'px-3 pt-3': !containedInPreview,
      'px-0 pt-3': containedInPreview,
      'popular-link--v2': isEnabledWidgetV2,
    }"
  >
    <div class="popular-link__inner">
      <div class="popular-link__title">
        {{ $t('RESOURCE_LIST.POPULAR_LINKS') }}
      </div>
      <ul :class="{ 'ml-0 mb-0': containedInPreview }">
        <!-- V2: Simplified template with CSS-only hover -->
        <template v-if="isEnabledWidgetV2">
          <li
            v-for="resource in resources"
            :key="resource.id"
            class="popular-link__list"
          >
            <div
              class="popular-link__item"
              @click.prevent="onResourceClicked(resource)"
            >
              <span class="popular-link__item-text">{{
                resourceTitle(resource)
              }}</span>
              <Icon
                class="popular-link__icon"
                size="16"
                icon="arrow-right-new"
                view-box="0 0 16 16"
                :icons="iconLib"
              />
            </div>
          </li>
        </template>

        <!-- V1: Original template with JS hover handlers -->
        <template v-else>
          <li
            v-for="(resource, index) in resources"
            :key="resource.id"
            class="popular-link__list"
            @mouseover="handleMouseOver(index)"
            @mouseleave="handleMouseLeave(index)"
          >
            <div
              class="popular-link__item"
              @click.prevent="onResourceClicked(resource)"
            >
              <span ref="listText" class="popular-link__item-text">{{
                resourceTitle(resource)
              }}</span>
              <Icon
                ref="listIcon"
                class="popular-link__icon"
                size="16"
                icon="arrow-right-new"
                view-box="0 0 16 16"
                :icons="iconLib"
              />
            </div>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Icon from '../../shared/components/FluentIcon/Icon.vue';
import icons from '../../shared/components/FluentIcon/icons.json';
import { useFlipper } from 'widget/composables/useFlipper';
import {
  PRIMARY,
  SECONDARY,
  ACCENT,
} from '../../../javascript/widget/constants/colors.js';

const WIDGET = 'widget';
const PREVIEW = 'preview';

export default {
  components: {
    Icon,
  },
  setup() {
    const { isEnabled } = useFlipper();
    return { isFlipperEnabled: isEnabled };
  },
  props: {
    containedIn: {
      type: String,
      default: WIDGET,
    },
    color: {
      type: String,
      default: ACCENT,
    },
  },
  computed: {
    ...mapGetters({
      resources: 'inboxChatResources/getActiveResources',
      widgetColor: 'appConfig/getWidgetColor',
    }),
    iconLib() {
      return icons;
    },
    containedInPreview() {
      return this.containedIn === PREVIEW;
    },
    computedColor() {
      if (this.containedIn === WIDGET) return this.widgetColor;
      return this.color;
    },
    isEnabledWidgetV2() {
      return this.isFlipperEnabled('chat_widget_v2').value;
    },
  },
  created() {
    if (this.containedIn === WIDGET) {
      this.$store.dispatch(
        'inboxChatResources/get',
        window.chatwootWebChannel.websiteToken
      );
    }
  },
  methods: {
    resourceTitle(resource) {
      if (resource.resource_type === 'Article') {
        return resource.resource.title;
      }
      return resource.resource.name;
    },
    onResourceClicked(resource) {
      if (resource.resource_type === 'Article') {
        this.$emit('view-article', resource.resource.api_url);
      }
    },
    // V1 methods - JS-based hover effects
    handleMouseOver(index) {
      if (this.isEnabledWidgetV2) return;
      const text = this.$refs.listText[index];
      const icon = this.$refs.listIcon[index];
      if (text && icon) {
        text.style.color = 'var(--brand-text-on-white-background)';
        this.$nextTick(() => {
          icon.$el.style.color = 'var(--brand-text-on-white-background)';
        });
      }
    },
    handleMouseLeave(index) {
      if (this.isEnabledWidgetV2) return;
      const text = this.$refs.listText[index];
      const icon = this.$refs.listIcon[index];
      if (text && icon) {
        text.style.color = PRIMARY;
        this.$nextTick(() => {
          icon.$el.style.color = SECONDARY;
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// =====================
// V1 Styles (Default)
// =====================
.popular-link {
  @apply w-full;

  &__inner {
    @apply shadow-sm rounded-md bg-neutral overflow-hidden;
  }

  &__title {
    @apply text-sm font-semibold px-5 py-4 text-primary;
  }

  &__list {
    @apply border-t border-solid border-lightgray flex content-center cursor-pointer px-5 py-3 transition-all ease-in-out duration-200;

    &:hover {
      @apply bg-neutral-subtle;
    }
  }

  &__item {
    @apply flex items-center justify-between w-full text-sm;
  }

  &__item-text {
    @apply flex-1 text-primary transition-all ease-in-out duration-200;
  }

  &__icon {
    @apply ml-3 transition-all ease-in-out duration-200 text-secondary;
  }
}

// =====================
// V2 Styles (Override)
// =====================
.popular-link--v2 {
  .popular-link__inner {
    @apply rounded-lg bg-white shadow-none;
    box-shadow: 0 1.004px 3.012px 0 rgba(20, 4, 40, 0.08);
  }

  .popular-link__title {
    @apply px-4 py-2 text-slate-900;
  }

  .popular-link__list {
    @apply border-slate-200 select-none px-4 py-2;

    &:hover {
      @apply bg-slate-50;

      .popular-link__item-text,
      .popular-link__icon {
        color: var(--brand-text-on-white-background);
      }
    }

    &:active {
      @apply bg-slate-100;
    }
  }

  .popular-link__item-text {
    @apply text-slate-900;
  }

  .popular-link__icon {
    @apply flex-none text-slate-500;
  }
}
</style>
